'use client'

import Link from 'next/link'
import { useState } from 'react'
import { usePathname } from 'next/navigation'

type Promo = {
  id: string
  name: string
  description: string | null
  redemptionType: 'PERCENT_DISCOUNT' | 'FIXED_DISCOUNT' | 'WALLET_CREDIT' | 'FREE_MINUTES'
  redemptionValue: string
  validUntil: string
  partner: { id: string; name: string; type: string; logoUrl: string | null }
}

function readCsrfCookie(): string | null {
  const m = document.cookie.match(/(?:^|; )__Host-buyer\.csrf=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

function fmt(p: Promo): string {
  const n = Number(p.redemptionValue)
  if (p.redemptionType === 'PERCENT_DISCOUNT') return `${n}% off`
  if (p.redemptionType === 'FIXED_DISCOUNT') return `LKR ${n.toLocaleString()} off`
  if (p.redemptionType === 'WALLET_CREDIT') return `LKR ${n.toLocaleString()} wallet credit`
  if (p.redemptionType === 'FREE_MINUTES') return `${n} free minutes`
  return p.redemptionType
}

export function PromoList({ items, signedIn }: { items: Promo[]; signedIn: boolean }) {
  if (items.length === 0) {
    return (
      <div className="bg-navy-2 border border-[rgba(255,255,255,0.07)] rounded p-12 text-center text-sm text-text-3">
        No active platform promos right now. Check back soon.
      </div>
    )
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((p) => (
        <PromoCard key={p.id} promo={p} signedIn={signedIn} />
      ))}
    </div>
  )
}

function PromoCard({ promo, signedIn }: { promo: Promo; signedIn: boolean }) {
  const [busy, setBusy] = useState(false)
  const [code, setCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pathname = usePathname()

  async function claim() {
    if (busy) return
    setBusy(true)
    setError(null)
    const csrf = readCsrfCookie()
    if (!csrf) {
      setError('Sign in again.')
      setBusy(false)
      return
    }
    try {
      const res = await fetch(`/api/v1/marketplace/coupons/${promo.id}/claim`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrf },
      })
      const data = (await res.json().catch(() => null)) as
        | { issuance?: { couponCode: string }; error?: string }
        | null
      if (!res.ok || !data?.issuance) {
        setError(data?.error ?? 'Could not claim.')
        return
      }
      setCode(data.issuance.couponCode)
    } catch {
      setError('Network error.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="bg-navy-2 border border-amber/30 rounded p-4 flex flex-col">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 shrink-0 rounded bg-navy-3 overflow-hidden flex items-center justify-center text-text-3 text-xl">
          {promo.partner.logoUrl ? (
            /* eslint-disable-next-line @next/next/no-img-element */
            <img src={promo.partner.logoUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            '🎁'
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{promo.name}</div>
          <div className="text-[10px] text-text-3 font-mono uppercase tracking-widest">
            {promo.partner.name}
          </div>
        </div>
      </div>
      <div className="text-amber font-semibold text-base mb-2">{fmt(promo)}</div>
      {promo.description && (
        <div className="text-xs text-text-2 mb-3 flex-1 line-clamp-3">{promo.description}</div>
      )}
      <div className="text-[10px] text-text-3 font-mono mb-3">
        Valid until {new Date(promo.validUntil).toLocaleDateString('en-LK', { month: 'short', day: 'numeric' })}
      </div>
      {code ? (
        <div className="bg-green/10 border border-green/30 rounded p-2 text-center">
          <div className="text-[10px] text-text-3 font-mono uppercase tracking-widest mb-0.5">
            Your code
          </div>
          <div className="font-mono text-base font-bold tracking-widest text-green">{code}</div>
          <Link href="/me/coupons" className="text-[10px] text-cyan hover:underline mt-1 inline-block">
            View all →
          </Link>
        </div>
      ) : (
        <>
          {error && (
            <div className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded px-2 py-1 mb-2">
              {error}
            </div>
          )}
          {signedIn ? (
            <button
              type="button"
              onClick={claim}
              disabled={busy}
              className="w-full px-3 py-2 bg-amber/90 text-navy text-xs font-semibold rounded hover:brightness-95 disabled:opacity-40"
            >
              {busy ? 'Claiming…' : 'Claim'}
            </button>
          ) : (
            <Link
              href={`/auth/buyer-signin?next=${encodeURIComponent(pathname)}`}
              className="block w-full text-center px-3 py-2 bg-amber/90 text-navy text-xs font-semibold rounded hover:brightness-95"
            >
              Sign in to claim
            </Link>
          )}
        </>
      )}
    </div>
  )
}
